import { Layout } from "@/components/Layout";
import { Helmet } from "react-helmet-async";
import { Mail, Phone, Download, ImageIcon } from "lucide-react";
import louisePhoto from "@/assets/louise-contact.jpg";
import pressPortrait from "@/assets/press-louise-portrait.jpg";
import pressPortrait2 from "@/assets/press-louise-portrait-2.jpg";
import pressSkapaAward from "@/assets/press-skapa-award.jpg";
import pressSkapaCeremony from "@/assets/press-skapa-ceremony.jpg";
import pressSkapaCeremony2 from "@/assets/press-skapa-ceremony-2.jpg";
import pressPortrait3 from "@/assets/press-louise-portrait-3.jpg";
import logoBlack from "@/assets/logo-black.png";
import logoWhite from "@/assets/logo-white.png";

const pressImages: { title: string; note: string; src?: string; fileName?: string }[] = [
  {
    title: "Louise Wintzell, CEO & Founder",
    note: "Portrait, suitable for interviews and features",
    src: pressPortrait,
    fileName: "hidnstock-louise-wintzell-portrait.jpg",
  },
  {
    title: "Louise Wintzell, CEO & Founder",
    note: "Portrait, wooden backdrop",
    src: pressPortrait2,
    fileName: "hidnstock-louise-wintzell-portrait-2.jpg",
  },
  {
    title: "SKAPA innovation award",
    note: "Louise Wintzell receiving the SKAPA prize",
    src: pressSkapaAward,
    fileName: "hidnstock-skapa-award.jpg",
  },
  {
    title: "SKAPA award ceremony",
    note: "The award ceremony, Halland 2026",
    src: pressSkapaCeremony,
    fileName: "hidnstock-skapa-ceremony.jpg",
  },
  {
    title: "SKAPA award ceremony, Halland 2026",
    note: "Louise Wintzell with the SKAPA certificate",
    src: pressSkapaCeremony2,
    fileName: "hidnstock-skapa-ceremony-2.jpg",
  },
  {
    title: "Louise Wintzell, CEO & Founder",
    note: "Portrait, leather jacket",
    src: pressPortrait3,
    fileName: "hidnstock-louise-wintzell-portrait-3.jpg",
  },
];

const Press = () => {
  return (
    <Layout>
      <Helmet>
        <title>Press — HIDNstock</title>
        <meta name="description" content="Press resources for HIDNstock: brand images, platform screenshots, logo and press contact. The B2B marketplace for deadstock and sorted secondhand textiles." />
        <link rel="canonical" href="https://hidnstock.com/press" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hidnstock.com/press" />
        <meta property="og:title" content="Press — HIDNstock" />
        <meta property="og:description" content="Press images, logo and contact for HIDNstock, the B2B marketplace for circular fashion." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="relative py-24 md:py-32 bg-gradient-sand bg-grain overflow-hidden border-b-4 border-foreground">
          <span className="deco-text absolute -bottom-6 left-0 text-[22vw] leading-none" aria-hidden="true">PRESS</span>
          <div className="container mx-auto px-6 lg:px-8 max-w-4xl relative">
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-foreground/60 mb-5">06 — Press room</p>
            <h1 className="text-foreground mb-6 font-black">Press &amp; media</h1>
            <p className="text-xl text-muted-foreground max-w-2xl">
              Everything you need to write about HIDNstock: brand images, platform screenshots, logo and a direct line to our press contact.
            </p>
          </div>
        </section>

        {/* Boilerplate */}
        <section className="py-20 md:py-28 border-b-4 border-foreground">
          <div className="container mx-auto px-6 lg:px-8 max-w-3xl">
            <div className="rule-ink pt-10">
              <h2 className="text-foreground mb-3 font-bold">About HIDNstock</h2>
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-foreground/60 mb-6">Where excess meets creativity</p>
              <p className="text-lg text-foreground/80 leading-relaxed mb-4">
                HIDNstock is the B2B marketplace where excess meets creativity. It's where fashion brands, sorting facilities and redesigners trade sorted secondhand, unsold products and excess inventory in bulk - and a remake project platform, where brands post remake projects that turn surplus material into new products.
              </p>
              <p className="text-lg text-foreground/80 leading-relaxed">
                By connecting supply and demand for circular textiles, HIDNstock helps the fashion industry keep valuable material in play instead of in storage or incineration.
              </p>
            </div>
          </div>
        </section>

        {/* Press images */}
        <section className="py-20 md:py-28 bg-sand bg-grain border-b-4 border-foreground">
          <div className="container mx-auto px-6 lg:px-8 max-w-5xl">
            <h2 className="text-foreground mb-3 font-bold">Press images</h2>
            <p className="text-muted-foreground mb-10 max-w-2xl">
              High-resolution images for editorial use. Credit "HIDNstock" when publishing.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {pressImages.map((img) => (
                <div key={img.fileName ?? img.title} className="bg-card rounded-xl border border-border shadow-soft overflow-hidden">
                  {img.src ? (
                    <div className="aspect-[4/3] bg-muted overflow-hidden">
                      <img src={img.src} alt={img.title} className="h-full w-full object-cover object-top" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-muted flex flex-col items-center justify-center gap-3 text-muted-foreground">
                      <ImageIcon className="h-10 w-10" strokeWidth={1.5} />
                      <span className="text-sm uppercase font-medium">Image coming soon</span>
                    </div>
                  )}
                  <div className="p-5 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-base font-medium text-foreground">{img.title}</h3>
                      <p className="text-sm text-muted-foreground">{img.note}</p>
                    </div>
                    {img.src ? (
                      <a
                        href={img.src}
                        download={img.fileName}
                        className="inline-flex items-center gap-2 text-xs font-bold uppercase text-foreground border-2 border-foreground px-3 py-1.5 hover:bg-foreground hover:text-background transition-colors shrink-0"
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </a>
                    ) : (
                      <button
                        type="button"
                        disabled
                        className="inline-flex items-center gap-2 text-sm font-medium uppercase text-muted-foreground/60 cursor-not-allowed shrink-0"
                        title="Available when images are added"
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Logo */}
        <section className="py-20 md:py-28 border-b-4 border-foreground">
          <div className="container mx-auto px-6 lg:px-8 max-w-3xl">
            <div className="rule-ink pt-10">
              <h2 className="text-foreground mb-3 font-bold">Logo</h2>
              <p className="text-muted-foreground mb-8">
                The HIDNstock logo in light and dark versions. Please do not alter proportions, colors or spacing.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl border border-border shadow-soft p-10 flex flex-col items-center gap-6">
                  <img src={logoBlack} alt="HIDNstock logo, black" className="h-12 w-auto" />
                  <a
                    href={logoBlack}
                    download="hidnstock-logo-black.png"
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase text-foreground border-2 border-foreground px-3 py-1.5 hover:bg-foreground hover:text-background transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </a>
                </div>
                <div className="bg-primary rounded-xl shadow-soft p-10 flex flex-col items-center gap-6">
                  <img src={logoWhite} alt="HIDNstock logo, white" className="h-12 w-auto" />
                  <a
                    href={logoWhite}
                    download="hidnstock-logo-white.png"
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase text-primary-foreground border-2 border-primary-foreground px-3 py-1.5 hover:bg-primary-foreground hover:text-primary transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Press contact */}
        <section className="py-20 md:py-28 bg-sand-deep bg-grain">
          <div className="container mx-auto px-6 lg:px-8 max-w-3xl">
            <h2 className="text-foreground mb-3 font-bold">Press contact</h2>
            <p className="text-muted-foreground mb-8">
              For interviews, comments, image requests or anything else press related, reach out directly.
            </p>
            <div className="bg-card rounded-xl border border-border shadow-medium p-8 md:p-10 flex flex-col sm:flex-row sm:items-center gap-6">
              <img
                src={louisePhoto}
                alt="Louise Wintzell, CEO and Founder of HIDNstock"
                className="h-20 w-20 rounded-full object-cover shrink-0"
              />
              <div className="flex-1">
                <h3 className="text-xl font-medium text-foreground">Louise Wintzell</h3>
                <p className="text-muted-foreground">CEO and Founder, HIDNstock</p>
                <a
                  href="tel:+46706620630"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mt-1"
                >
                  <Phone className="h-4 w-4" />
                  +46 70 662 06 30
                </a>
              </div>
              <a
                href="mailto:louise@hidnstock.com"
                className="inline-flex items-center gap-2 bg-foreground text-background px-6 py-3 font-bold uppercase text-sm border-2 border-foreground hover:bg-transparent hover:text-foreground transition-colors"
              >
                <Mail className="h-4 w-4" />
                louise@hidnstock.com
              </a>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Press;
